import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CmsBackgroundComponent } from './cms-background.component';
import { ConnectivityService } from '../connectivity/connectivity.service';

/**
 * App-wide "Connection lost" / "No internet connection" overlay — mounted once
 * in app.ts as a sibling after `<router-outlet>` (same spot as the toast
 * container / route-loading overlay) so it can appear over ANY active screen,
 * including the VDI iframe, without each component needing to know about it.
 *
 * Driven entirely by {@link ConnectivityService}.status:
 *  - 'online'   -> renders nothing; the underlying route is untouched and simply
 *                  resumes exactly where it was, since it was never unmounted.
 *  - 'checking' -> Figma "Connection lost" screen (spinner, reconnecting copy).
 *                  Shown both for the initial automatic detection AND while a
 *                  manual Retry (from the offline screen below) is in flight.
 *  - 'offline'  -> Figma "No internet connection" screen (Retry button). Per
 *                  spec, there is deliberately no "Open network settings"
 *                  button here.
 *
 * Figma: node 1105-24628 ("Connection lost") / 1105-24614 ("No internet
 * connection"), file GDwo6VlVp4pOT2DFbBdZmn.
 *
 * Deliberately self-contained against the exact failure this screen exists to
 * handle:
 *  - Icons are inlined `<svg>` markup, not `<img src="/icons/...">`. An `<img>`
 *    is a separate network/cache fetch that can itself fail while offline
 *    (broken-image icon); inline SVG ships inside this already-loaded JS
 *    bundle, so it can never 404 regardless of service-worker/cache state.
 *  - Text goes through `t()` below instead of the `| translate` pipe. If the
 *    `/i18n/<lang>.json` fetch hasn't completed (e.g. this is the very first,
 *    offline-from-the-start load, before any service worker has had a chance
 *    to cache it), ngx-translate has nothing loaded and the pipe renders the
 *    raw key string (e.g. "common.connectivity.noInternetTitle") instead of
 *    real copy. `t()` detects that (translate.instant() returns the key
 *    unchanged when it can't resolve it) and falls back to hardcoded English
 *    copy so this screen never shows a raw i18n key — it still prefers the
 *    real, loaded translation whenever one is available.
 */
@Component({
  selector: 'app-offline-overlay',
  standalone: true,
  imports: [CmsBackgroundComponent],
  template: `
    @if (connectivity.isOffline()) {
      <div class="fixed inset-0 z-[10000]">
        <app-cms-background
          fallback="banner"
          containerClass="flex min-h-dvh items-center justify-center px-6"
          overlayClass="bg-[rgba(20,20,20,0.8)] backdrop-blur-[24px]"
        >
          @if (connectivity.status() === 'checking') {
            <!-- Figma: "Connection lost" (1105-24628) -->
            <div class="flex flex-col items-center gap-6 text-center">
              <svg
                class="h-12 w-12 animate-spin"
                viewBox="0 0 48 48"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="24" cy="24" r="20" stroke="#5A5A5A" stroke-width="8" />
                <path
                  d="M24 4C28.2236 4 32.3388 5.3371 35.7557 7.81966C39.1727 10.3022 41.716 13.8028 43.0211 17.8197C44.3263 21.8365 44.3263 26.1635 43.0211 30.1803C41.716 34.1972 39.1727 37.6978 35.7557 40.1803"
                  stroke="#AA77E2"
                  stroke-width="8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <div class="flex flex-col items-center gap-4">
                <h1 class="text-hero font-black tracking-[-0.03em] text-white">
                  {{ t('common.connectivity.connectionLostTitle', 'Connection lost') }}
                </h1>
                <p class="max-w-[560px] text-subtitle text-white/[0.78]">
                  {{
                    t(
                      'common.connectivity.connectionLostBody',
                      "We are trying to reconnect — rest assured you won't lose your progress"
                    )
                  }}
                </p>
              </div>
            </div>
          } @else {
            <!-- Figma: "No internet connection" (1105-24614) -->
            <div class="flex w-full max-w-[663px] flex-col items-center gap-16">
              <div class="flex flex-col items-center gap-6">
                <div class="flex size-20 items-center justify-center rounded-full bg-[rgba(89,89,89,0.5)]">
                  <svg class="h-9 w-10" viewBox="0 0 38.679 35.1944" fill="none" aria-hidden="true">
                    <path
                      d="M6.14392 19.2172C6.31463 19.4168 6.52299 19.5808 6.7571 19.6999C6.99122 19.819 7.24649 19.8908 7.50836 19.9113C7.77022 19.9317 8.03355 19.9003 8.28329 19.819C8.53304 19.7376 8.76432 19.6079 8.96392 19.4372L9.20392 19.2372L16.6639 11.7972C12.8622 12.317 9.28838 13.9131 6.36392 16.3972C6.1643 16.5679 6.00026 16.7762 5.88117 17.0104C5.76208 17.2445 5.69027 17.4997 5.66984 17.7616C5.64941 18.0235 5.68076 18.2868 5.76211 18.5365C5.84345 18.7863 5.97319 19.0176 6.14392 19.2172V19.2172ZM3.34392 13.7572C7.73087 9.79407 13.4319 7.59916 19.3439 7.59718H20.7839L24.4039 3.99718C22.7325 3.71372 21.0391 3.57986 19.3439 3.59718C12.4454 3.60763 5.79349 6.1644 0.663924 10.7772C0.268751 11.1326 0.0309421 11.6304 0.00281164 12.1611C-0.0253188 12.6918 0.158534 13.212 0.513924 13.6072C0.869314 14.0024 1.36713 14.2402 1.89786 14.2683C2.42859 14.2964 2.94875 14.1126 3.34392 13.7572V13.7572ZM38.0239 10.7772C36.4197 9.34437 34.6546 8.10272 32.7639 7.07718L36.3239 3.59718C36.5196 3.40151 36.6748 3.16922 36.7807 2.91356C36.8866 2.65791 36.9411 2.3839 36.9411 2.10718C36.9411 1.83046 36.8866 1.55645 36.7807 1.3008C36.6748 1.04514 36.5196 0.812848 36.3239 0.617178C36.1283 0.421508 35.896 0.266295 35.6403 0.160399C35.3847 0.0545033 35.1106 0 34.8339 0C34.5572 0 34.2832 0.0545033 34.0275 0.160399C33.7719 0.266295 33.5396 0.421508 33.3439 0.617178L2.36392 31.5972C2.16825 31.7928 2.01304 32.0251 1.90714 32.2808C1.80125 32.5365 1.74675 32.8105 1.74675 33.0872C1.74675 33.3639 1.80125 33.6379 1.90714 33.8936C2.01304 34.1492 2.16825 34.3815 2.36392 34.5772C2.7591 34.9724 3.29507 35.1944 3.85392 35.1944C4.13064 35.1944 4.40465 35.1399 4.66031 35.034C4.91596 34.9281 5.14825 34.7728 5.34392 34.5772L14.7439 25.1572C16.1159 24.1514 17.7728 23.6091 19.4739 23.6091C21.1751 23.6091 22.832 24.1514 24.2039 25.1572C24.5501 25.4168 24.9712 25.5572 25.4039 25.5572C25.7144 25.5572 26.0206 25.4849 26.2984 25.346C26.5761 25.2072 26.8176 25.0056 27.0039 24.7572C27.1615 24.5471 27.2762 24.308 27.3414 24.0535C27.4065 23.7991 27.421 23.5343 27.3838 23.2743C27.3467 23.0143 27.2587 22.7642 27.1249 22.5382C26.9911 22.3122 26.814 22.1148 26.6039 21.9572C24.7393 20.5823 22.5165 19.7765 20.2039 19.6372L23.7239 16.1372C25.9335 16.7735 27.98 17.8786 29.7239 19.3772C30.1271 19.722 30.6506 19.8925 31.1795 19.8512C31.7083 19.81 32.1991 19.5603 32.5439 19.1572C32.8887 18.754 33.0592 18.2305 33.018 17.7016C32.9767 17.1728 32.7271 16.682 32.3239 16.3372C30.6679 14.9243 28.7891 13.7957 26.7639 12.9972L29.8039 9.95718C31.8204 10.9565 33.6855 12.2358 35.3439 13.7572C35.7137 14.0833 36.1909 14.2614 36.6839 14.2572C36.963 14.2565 37.2389 14.1975 37.4938 14.0838C37.7487 13.9701 37.9769 13.8044 38.1639 13.5972C38.5184 13.2042 38.7028 12.6868 38.6765 12.1583C38.6503 11.6297 38.4156 11.1331 38.0239 10.7772V10.7772ZM19.3439 27.5972C18.7506 27.5972 18.1706 27.7731 17.6772 28.1028C17.1839 28.4324 16.7993 28.901 16.5723 29.4491C16.3452 29.9973 16.2858 30.6005 16.4016 31.1824C16.5173 31.7644 16.803 32.2989 17.2226 32.7185C17.6422 33.1381 18.1767 33.4238 18.7587 33.5395C19.3406 33.6553 19.9438 33.5959 20.492 33.3688C21.0402 33.1418 21.5087 32.7572 21.8383 32.2639C22.168 31.7705 22.3439 31.1905 22.3439 30.5972C22.3439 29.8015 22.0279 29.0385 21.4652 28.4759C20.9026 27.9132 20.1396 27.5972 19.3439 27.5972Z"
                      fill="#FF5256"
                    />
                  </svg>
                </div>
                <div class="flex flex-col items-center gap-4">
                  <h1 class="text-hero font-black tracking-[-0.03em] text-white">
                    {{ t('common.connectivity.noInternetTitle', 'No internet connection') }}
                  </h1>
                  <!--
                    Body copy deliberately says "your internet connection", not "your set top
                    box internet connection": the STB wording came across from Consumer, which
                    also ships on the set-top box — this PWA never does, so the connection to
                    check is always the user's own device's.
                    Keep this literal in sync with common.connectivity.noInternetBody in
                    public/i18n/en.json; it is the fallback when that key is missing.
                  -->
                  <p class="text-center text-subtitle text-white/[0.78]">
                    {{
                      t(
                        'common.connectivity.noInternetBody',
                        'Check your internet connection and try again.'
                      )
                    }}
                  </p>
                </div>
              </div>
              <button
                type="button"
                (click)="connectivity.retry()"
                class="hover:cursor-pointer w-full max-w-[325px] rounded-full bg-brand-purple-dark px-8 py-4 text-button-lg text-white transition-colors hover:bg-brand-purple"
              >
                {{ t('common.connectivity.retry', 'Retry') }}
              </button>
            </div>
            <!--
              No Help CTA here, deliberately. HelpButton opens the CMS faq_url/user_guide_url
              with window.open(url, '_blank') — offline that lands the user on Chrome's own
              network-error page in a new tab, which is the reported bug. With no URL cached it
              instead raises a toast, and ToastContainer sits below this overlay's z-[10000], so
              the click looks dead. Neither outcome is usable without a connection, so the CTA is
              removed from this screen rather than papered over. Retry (above) is the only action.
            -->
          }
        </app-cms-background>
      </div>
    }
  `,
})
export class OfflineOverlayComponent {
  connectivity = inject(ConnectivityService);
  private translate = inject(TranslateService);

  /**
   * `translate.instant()` returns the key itself, unchanged, when it can't
   * resolve a translation (translations not loaded yet, or the specific key
   * is missing) — that equality is what we detect to fall back to hardcoded
   * copy. See the class doc comment above for why this screen specifically
   * can't rely solely on the network-loaded `| translate` pipe.
   */
  t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
